import { css } from '@emotion/react';
import { LoginWithIdentityProviderErrors } from '@zapier/authentication/src/hooks/useLoginWithSAMLSSO';
import { Button, TextInput } from '@zapier/marketing-ui';
import { FormEvent, useCallback, useEffect, useRef } from 'react';

import {
  dispatchSAMLSSOFormFocussedEvent,
  dispatchSAMLSSOFormSubmittedEvent,
} from '../../avro';
import { LoginStatus } from '../../hooks';
import { useAutoFocus } from '../../hooks/useAutoFocus';

type Props = {
  canSubmit: boolean;
  email: string;
  formErrorId?: string;
  formErrors: LoginWithIdentityProviderErrors;
  handleChange: (email: string) => void;
  handleSubmit: () => void;
  shouldFocusEmailField: boolean;
  shouldDisableEmailField: boolean;
  status: LoginStatus;
};

const fieldCss = css`
  margin-bottom: 20px;
`;

export function EmailForm({
  canSubmit,
  formErrorId,
  formErrors,
  handleChange,
  handleSubmit,
  shouldFocusEmailField,
  status,
  email,
  shouldDisableEmailField,
}: Props) {
  // Used to focus email field when we first switch to the SSO login page
  const emailRef = useAutoFocus(shouldFocusEmailField);

  const submitRef = useRef<HTMLButtonElement>(null);

  const hasError = status === 'error';

  useEffect(() => {
    if (!hasError) {
      return;
    }

    // Submit drops focus to <body>; refocus the email field, or the submit button when it's disabled (SC 2.4.3).
    const target = emailRef.current?.disabled
      ? submitRef.current
      : emailRef.current;
    target?.focus();
  }, [hasError, emailRef]);

  const _handleChange = useCallback(
    (value: string) => {
      return handleChange(value);
    },
    [handleChange]
  );

  const hasTrackedFocus = useRef(false);

  const _handleFocus = useCallback(() => {
    if (hasTrackedFocus.current === true) return;

    dispatchSAMLSSOFormFocussedEvent();
    hasTrackedFocus.current = true;
  }, []);

  const _handleSubmit = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      dispatchSAMLSSOFormSubmittedEvent();
      handleSubmit();
    },
    [handleSubmit]
  );

  return (
    <form onSubmit={_handleSubmit} onFocus={_handleFocus}>
      <div css={[fieldCss]} data-testid="emailField-wrapper">
        <TextInput
          ref={emailRef}
          id="login-email"
          name="email"
          label="Email"
          type="email"
          placeholder="Email"
          value={email}
          required={true}
          disabled={shouldDisableEmailField || status === 'submitting'}
          error={status === 'error' ? formErrors.email : ''}
          autoComplete="email"
          inputMode="email"
          onChange={_handleChange}
        />
      </div>
      <Button
        ref={submitRef}
        // Focus can land here on failure and drown out the alert in VoiceOver, so the button carries the error too.
        aria-describedby={formErrorId}
        fullWidth={true}
        variant="brand"
        disabled={!canSubmit || status === 'submitting'}
        type="submit"
        id="login-submit"
      >
        <span data-testid="email-continue">Continue with SSO</span>
      </Button>
    </form>
  );
}
